import { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { logAdminActivity } from '../services/adminActivityLog';

/**
 * Authentication Context & Hook
 * Manages user authentication state with Firebase
 * Only allows admin_master and admin types to access the panel
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Listen to Firebase Auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // Fetch user profile from Firestore (users collection)
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Verify user type is admin_master, admin, or operator
            if (
              userData.type === 'admin_master' ||
              userData.type === 'admin' ||
              userData.type === 'operator'
            ) {
              setUser({
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                name: userData.name || userData.full_name || 'Admin',
                type: userData.type,
                createdAt: userData.createdAt || userData.created_at,
                updatedAt: userData.updatedAt || userData.updated_at,
              });
              setError(null);
            } else {
              // Not an admin, sign out
              await firebaseSignOut(auth);
              setUser(null);
              setError('Access denied. Admin privileges required.');
            }
          } else {
            // User document doesn't exist
            await firebaseSignOut(auth);
            setUser(null);
            setError('User profile not found.');
          }
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setError(err.message);
        setUser(null);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      // Sign in with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if user is admin (in users collection)
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        await firebaseSignOut(auth);
        throw new Error('User profile not found.');
      }
      
      const userData = userDoc.data();
      
      // Verify admin type
      if (
        userData.type !== 'admin_master' &&
        userData.type !== 'admin' &&
        userData.type !== 'operator'
      ) {
        await firebaseSignOut(auth);
        throw new Error('Access denied. Admin privileges required.');
      }

      // Audit login event. Construct the actor from the doc we just
      // read — the AuthProvider's `user` state hasn't been set yet
      // when this runs.
      logAdminActivity({
        actor: {
          uid: userCredential.user.uid,
          name: userData.name,
          email: userData.email || userCredential.user.email,
          type: userData.type,
        },
        action: 'login',
      });

      return { success: true };
    } catch (err) {
      console.error('Sign in error:', err);
      
      // 🔒 SECURITY: Sanitize error messages - don't expose Firebase details to prevent information leakage
      // Generic error messages prevent attackers from knowing if email exists, server issues, etc.
      let userMessage = 'Invalid email or password';
      
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        userMessage = 'Invalid email or password';
      } else if (err.code === 'auth/too-many-requests') {
        userMessage = 'Too many failed attempts. Please try again later';
      } else if (err.code === 'auth/network-request-failed') {
        userMessage = 'Network error. Please check your connection';
      } else if (err.message.includes('Access denied') || err.message.includes('User profile not found')) {
        userMessage = err.message;
      }
      
      setError(userMessage);
      const sanitizedError = new Error(userMessage);
      sanitizedError.code = 'auth/generic-error';
      throw sanitizedError;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      // Snapshot the current user BEFORE we tear down auth — otherwise
      // the write races against the auth state change and either
      // hits a permission-denied (rules see null) or has no actor.
      const actorSnapshot = user;
      if (actorSnapshot) {
        try {
          await logAdminActivity({ actor: actorSnapshot, action: 'logout' });
        } catch (_) { /* never block sign-out on audit failure */ }
      }
      await firebaseSignOut(auth);
      setUser(null);
      setError(null);
    } catch (err) {
      console.error('Sign out error:', err);
      setError(err.message);
      throw err;
    }
  };

  const confirmMasterAdmin = async (password) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      
      return true;
    } catch (err) {
      console.error('Re-authentication error:', err);
      throw err;
    }
  };

  const value = {
    user,
    loading,
    initialized,
    error,
    signIn,
    signOut,
    confirmMasterAdmin,
    isAuthenticated: !!user,
    isMasterAdmin: user?.type === 'admin_master',
    isAdmin: user?.type === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 * Access authentication context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
